import { useState } from "react";
import { Link } from "react-router";
import { Eye, EyeOff, FilePlus2, MoreHorizontal, PenLine, Trash2 } from "lucide-react";
import type { WebsiteDefinition } from "@baze/schema";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Button, buttonClass } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Field } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { NativeSelect } from "@/components/ui/select";
import { SwitchRow } from "@/components/ui/switch";
import { Tooltip } from "@/components/ui/tooltip";
import { cn } from "@/lib/cn";
import { t } from "@/i18n/id";
import type { PagePreset } from "./model/ops";

const PRESETS: PagePreset[] = ["tentang", "layanan", "kontak", "donasi", "formulir", "kosong"];

type DialogName = "add" | "rename" | "remove" | null;

/**
 * Page picker plus add / rename / delete / menu visibility. The home page ("/") can be renamed but never
 * deleted or taken out of the menu.
 */
export function PagesBar({
  siteId,
  def,
  pageId,
  pageLimit,
  onPage,
  onAdd,
  onRename,
  onRemove,
  onToggleNav,
}: {
  siteId: string;
  def: WebsiteDefinition;
  pageId: string;
  /** Plan limit (pages per site); undefined while the site is loading. */
  pageLimit?: number;
  onPage: (id: string) => void;
  onAdd: (preset: PagePreset, title: string, inNav: boolean) => void;
  onRename: (id: string, title: string) => void;
  onRemove: (id: string) => void;
  onToggleNav: (id: string, show: boolean) => void;
}) {
  const page = def.pages.find((p) => p.id === pageId) ?? def.pages[0]!;
  const [dialog, setDialog] = useState<DialogName>(null);
  const [title, setTitle] = useState("");
  const [preset, setPreset] = useState<PagePreset>("tentang");
  const [inNav, setInNav] = useState(true);
  const atLimit = pageLimit !== undefined && def.pages.length >= pageLimit;
  const isHome = page.slug === "/";
  const hidden = !isHome && !!page.hideFromNav;

  const open = (name: DialogName) => {
    setTitle(name === "rename" ? page.title : name === "add" ? t.editor.pagePresets[preset] : "");
    if (name === "add") setInNav(true);
    setDialog(name);
  };
  const close = (next: boolean) => {
    if (!next) setDialog(null);
  };

  return (
    <div className="px-3 pt-3">
      <label className="text-[11px] font-semibold tracking-wide text-slate-500 uppercase" htmlFor="editor-page">
        {t.editor.pageSwitcher}
      </label>
      <div className="mt-1.5 flex items-center gap-1">
        <div className="min-w-0 flex-1">
          <NativeSelect id="editor-page" selectSize="sm" value={page.id} onChange={(e) => onPage(e.target.value)}>
            {def.pages.map((p) => (
              <option key={p.id} value={p.id}>
                {p.slug !== "/" && p.hideFromNav ? t.editor.pageNotInNavOption(p.title) : p.title}
              </option>
            ))}
          </NativeSelect>
        </div>
        <Tooltip content={t.editor.addPage} side="top">
          <Button variant="secondary" size="icon-sm" aria-label={t.editor.addPage} onClick={() => open("add")}>
            <FilePlus2 aria-hidden="true" />
          </Button>
        </Tooltip>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon-sm" aria-label={t.editor.pageActions}>
              <MoreHorizontal aria-hidden="true" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onSelect={() => open("rename")}>
              <PenLine aria-hidden="true" />
              {t.editor.renamePage}
            </DropdownMenuItem>
            <DropdownMenuItem disabled={isHome} onSelect={() => onToggleNav(page.id, hidden)}>
              {hidden ? <Eye aria-hidden="true" /> : <EyeOff aria-hidden="true" />}
              {isHome ? t.editor.homeAlwaysInNav : hidden ? t.editor.showPageInNav : t.editor.hidePageFromNav}
            </DropdownMenuItem>
            <DropdownMenuItem destructive disabled={isHome} onSelect={() => open("remove")}>
              <Trash2 aria-hidden="true" />
              {isHome ? t.editor.homeLocked : t.editor.removePage}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      {hidden ? (
        <div className="mt-2 rounded-xl bg-slate-50 px-3 py-2.5 ring-1 ring-slate-900/5">
          <p className="flex items-center gap-1.5 text-[13px] font-semibold text-slate-700">
            <EyeOff className="size-3.5 shrink-0 text-slate-400" aria-hidden="true" />
            {t.editor.pageNotInNav}
          </p>
          <p className="mt-1 text-xs leading-snug text-slate-500">{t.editor.pageNotInNavBody}</p>
          <button type="button" className="mt-1.5 text-xs font-semibold text-primary hover:underline" onClick={() => onToggleNav(page.id, true)}>
            {t.editor.showPageInNav}
          </button>
        </div>
      ) : null}

      <ConfirmDialog
        open={dialog === "add"}
        onOpenChange={close}
        title={t.editor.addPage}
        description={atLimit ? undefined : t.editor.addPageBody}
        confirmLabel={t.editor.addPage}
        onConfirm={() => {
          if (atLimit || !title.trim()) return;
          onAdd(preset, title.trim(), inNav);
          setDialog(null);
        }}
      >
        {atLimit ? (
          <div className="rounded-2xl bg-amber-50 p-4 text-sm text-amber-950 ring-1 ring-amber-200">
            <p>{t.editor.pageLimit(pageLimit!)}</p>
            <Link to={`/situs/${siteId}/checkout`} className={buttonClass("secondary", "sm", "mt-3")}>
              {t.editor.pageLimitCta}
            </Link>
          </div>
        ) : (
          <>
            <fieldset>
              <legend className="text-sm font-semibold text-slate-800">{t.editor.pagePresetLabel}</legend>
              <div className="mt-2 grid grid-cols-2 gap-2">
                {PRESETS.map((p) => (
                  <button
                    key={p}
                    type="button"
                    aria-pressed={preset === p}
                    className={cn(
                      "rounded-xl px-3 py-2.5 text-left ring-1 transition-colors",
                      preset === p ? "bg-primary-soft text-primary ring-primary" : "bg-white text-slate-700 ring-slate-900/10 hover:bg-slate-50",
                    )}
                    onClick={() => {
                      // Keep a typed name; replace the name only while it is still the previous preset's.
                      if (!title.trim() || title === t.editor.pagePresets[preset]) setTitle(t.editor.pagePresets[p]);
                      setPreset(p);
                    }}
                  >
                    <span className="block text-sm font-semibold">{t.editor.pagePresets[p]}</span>
                    <span className="block text-xs text-slate-500">{t.editor.pagePresetHints[p]}</span>
                  </button>
                ))}
              </div>
            </fieldset>
            <Field label={t.editor.pageName}>
              <Input value={title} maxLength={80} onChange={(e) => setTitle(e.target.value)} autoFocus />
            </Field>
            <SwitchRow id="new-page-nav" label={t.editor.pageInNav} hint={t.editor.pageInNavHint} checked={inNav} onCheckedChange={setInNav} />
          </>
        )}
      </ConfirmDialog>

      <ConfirmDialog
        open={dialog === "rename"}
        onOpenChange={close}
        title={t.editor.renamePage}
        description={t.editor.renamePageBody}
        confirmLabel={t.common.save}
        onConfirm={() => {
          if (!title.trim()) return;
          onRename(page.id, title.trim());
          setDialog(null);
        }}
      >
        <Field label={t.editor.pageName}>
          <Input value={title} maxLength={80} onChange={(e) => setTitle(e.target.value)} autoFocus />
        </Field>
      </ConfirmDialog>

      <ConfirmDialog
        open={dialog === "remove"}
        onOpenChange={close}
        title={t.editor.removePageTitle(page.title)}
        description={t.editor.removePageBody}
        confirmLabel={t.editor.removePage}
        destructive
        onConfirm={() => {
          onRemove(page.id);
          setDialog(null);
        }}
      />
    </div>
  );
}
